import { useEffect, useMemo, useState } from 'react';
import {
  Alert, Button, Card, Form,
  Input, InputNumber, message, Modal, Space, Tag, Tooltip, Typography, Upload,
} from 'antd';
import {
  CheckCircleOutlined, CheckOutlined, ClockCircleOutlined, CloseCircleOutlined,
  DeleteOutlined, EyeOutlined,
  FileExcelOutlined, FileImageOutlined, FileOutlined, FilePdfOutlined, FileWordOutlined,
  InfoCircleOutlined, SendOutlined, SyncOutlined, UploadOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import AppButton from '../AppButton';
import { formatFileSize, getFileExtension } from '../upload/bulkUploadValidation.js';
import { getPeriodBadgeConfig } from '../cards/cardConfig.js';
import {
  DEFAULT_HOURS_POLICY,
  computeHoursPolicy,
  reconcileHours,
  resolveDayKind,
} from './timesheetHoursPolicy.js';
import { scrollToFirstFormError } from '../../utils/scrollToFirstFormError.js';
import '../../styles/timesheet.css';

const { Text } = Typography;

// ── Status colour map ──────────────────────────────────────────────────────
const STATUS_COLOR = {
  draft:         'default',
  logged:        'blue',
  submitted:     'processing',
  approved:      'success',
  rejected:      'error',
  resubmitted:   'purple',
  reopened:      'warning',
  save_draft:    'default',
  pending:       'warning',
  not_submitted: 'default',
};

// Status badge label + icon — used both as ApprovalActionBar's own status cue
// and, exported, as the `headerExtra` badge on the Monthly Summary card
// (<DynamicCards moduleName="timesheetMonthlySummary" headerExtra={<StatusBadgeTag .../>} />).
export function StatusBadgeTag({ status }) {
  if (!status || status === 'draft' || status === 'not_submitted') return null;
  const map = {
    submitted:   { icon: <CheckOutlined />,        color: 'blue',   label: 'Submitted' },
    approved:    { icon: <CheckCircleOutlined />,   color: 'green',  label: 'Approved' },
    rejected:    { icon: <CloseCircleOutlined />,   color: 'red',    label: 'Rejected' },
    resubmitted: { icon: <SyncOutlined />,          color: 'purple', label: 'Re-Submitted' },
    reopened:    { icon: <SyncOutlined />,          color: 'orange', label: 'Reopened' },
    save_draft:  { icon: null,                      color: 'default',label: 'Draft' },
  };
  const cfg = map[status];
  if (!cfg) return <Tag color={STATUS_COLOR[status] ?? 'default'}>{status}</Tag>;
  return (
    <Tag color={cfg.color} icon={cfg.icon} style={{ borderRadius: 20, fontSize: 12, padding: '2px 10px' }}>
      {cfg.label}
    </Tag>
  );
}

// Standard vs Supplementary (or whatever the host app configures) — a
// timesheet logged for a calendar month before the current one reads as the
// "past" label, the current month (and `thresholdMonths` back) renders no
// badge at all: "Standard" is the implicit default, so only the exception
// (a late/supplementary month) needs calling out.
// Labels/colors/threshold come entirely from getPeriodBadgeConfig(configKey),
// which the host app can override via configureCoreTheme({ periodBadgeConfig }).
export function PeriodBadgeTag({ configKey = 'timesheet', month, year }) {
  const cfg = getPeriodBadgeConfig(configKey);
  const isPast = Boolean(month) && Boolean(year)
    && dayjs(`${year}-${month}-01`).isBefore(dayjs().subtract(cfg.thresholdMonths, 'month'), 'month');
  if (!isPast) return null;
  return <Tag color={cfg.pastColor}>{cfg.pastLabel}</Tag>;
}

// ── Log type CSS class ─────────────────────────────────────────────────────
const LOG_TYPE_CLASS = {
  working:  'timesheet-day-cell--working',
  overtime: 'timesheet-day-cell--overtime',
  leave:    'timesheet-day-cell--leave',
  holiday:  'timesheet-day-cell--holiday',
  weekoff:  'timesheet-day-cell--weekoff',
};


// ── Helpers ────────────────────────────────────────────────────────────────
function numberValue(v, fallback = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

function formatHoursShort(v) {
  const n = numberValue(v);
  return n === 0 ? '0h' : `${n.toFixed(1).replace(/\.0$/, '')}h`;
}

// Hour-split colours. Must stay in step with TIMESHEET_LEGEND on the consumer side.
const SPLIT_COLORS = {
  ST:    '#D0E6FF',
  OT:    '#AAFEBCA6',
  leave: '#FFECBA',
};

// Convert a desired AREA fraction into a gradient stop position.
//
// On a diagonal (135deg) gradient across a square, a stop at 25% does NOT colour 25%
// of the visible area — the corner is a triangle whose area grows as t². The area below
// a stop at axis-fraction t is 2t² (for t <= 0.5), so invert that to place the stop.
// Without this, 6h+2h would paint a stop at 75% but *look* like ~88% — the ratio the
// user is meant to read off the cell would be wrong.
function areaToStop(area, angle) {
  const a = Math.min(Math.max(area, 0), 1);
  // Straight (vertical/horizontal) gradients are already area-linear.
  if (!String(angle).startsWith('135') && !String(angle).startsWith('45')) return a;
  return a <= 0.5
    ? Math.sqrt(a / 2)
    : 1 - Math.sqrt((1 - a) / 2);
}

/**
 * buildDayGradient — background for a day cell, proportional to the hours logged.
 *
 * Returns null when the day has 0 or 1 hour types (a flat class colour is enough).
 * Exported so consumers can unit-test the ratio maths.
 */
export function buildDayGradient(day = {}, { angle = '135deg' } = {}) {
  const segments = [
    ['ST',    numberValue(day?.workingHours?.ST)],
    ['OT',    numberValue(day?.workingHours?.OT)],
    ['leave', numberValue(day?.leave?.CL) + numberValue(day?.leave?.SL)],
  ].filter(([, hours]) => hours > 0);

  const total = segments.reduce((sum, [, hours]) => sum + hours, 0);
  if (segments.length < 2 || total <= 0) return null; // single type -> solid class colour

  const stops = [];
  let cumulative = 0;
  segments.forEach(([key, hours], index) => {
    const from = areaToStop(cumulative / total, angle) * 100;
    cumulative += hours;
    const to = index === segments.length - 1
      ? 100
      : areaToStop(cumulative / total, angle) * 100;
    stops.push(`${SPLIT_COLORS[key]} ${from.toFixed(2)}% ${to.toFixed(2)}%`);
  });

  return `linear-gradient(${angle}, ${stops.join(', ')})`;
}

function getDayClass(day = {}, { isUnloggedHighlight = false, selected = false, selectable = false, reviewMode = false } = {}) {
  const st = numberValue(day?.workingHours?.ST);
  const ot = numberValue(day?.workingHours?.OT);
  const leave = numberValue(day?.leave?.CL) + numberValue(day?.leave?.SL);
  const isMixed = [st, ot, leave].filter((h) => h > 0).length > 1;

  // A mixed day gets its background from the inline computed gradient, so no log-type
  // colour class is applied (it would paint a flat colour over the split).
  // A day outside the resource's assignment on this project (before it started
  // or after it ended) reads as its own category — takes precedence over
  // week-off/holiday so a pre-assignment Saturday doesn't read as "day off"
  // (which implies an active engagement) when it's really "not your project yet".
  //
  // The single-type colour is derived from the actual ST/OT/Leave numbers,
  // NOT trusted from the stored `logType` string: a weekend day logged purely
  // as Overtime used to get saved with logType:"working" (DayLogModal's
  // submit() fell back to "working" whenever Leave was 0, regardless of
  // whether the hours were entered as Standard or Overtime), so it rendered
  // blue — Standard's colour — instead of Overtime's green, contradicting the
  // legend. Deriving it here fixes that for every already-saved record too,
  // not just ones saved after the submit()-side fix.
  const soleType = ot > 0 ? 'overtime' : st > 0 ? 'working' : leave > 0 ? 'leave' : null;
  const outOfRange = Boolean(day?.isOutOfAssignmentRange);
  const logType = outOfRange
    ? null
    : day?.isHoliday
      ? 'holiday'
      : soleType ?? (day?.isWeekOff ? 'weekoff' : null);
  const status = day?.status;

  return [
    'timesheet-day-cell',
    outOfRange ? 'timesheet-day-cell--out-of-range' : (!isMixed && logType ? (LOG_TYPE_CLASS[logType] ?? '') : ''),
    day?.isFuture              ? 'timesheet-day-cell--future'             : '',
    status === 'rejected'      ? 'timesheet-day-cell--rejected'           : '',
    status === 'approved'      ? 'timesheet-day-cell--approved'           : '',
    selected                   ? 'timesheet-day-cell--selected'           : '',
    isUnloggedHighlight        ? 'timesheet-day-cell--unlogged-highlight' : '',
    selectable                 ? 'timesheet-day-cell--selectable'         : '',
    reviewMode                 ? 'timesheet-day-cell--review'             : '',
  ].filter(Boolean).join(' ');
}

// ── TimesheetDayCell ───────────────────────────────────────────────────────
/**
 * A single day in the timesheet calendar.
 *
 * Three modes, all opt-in so existing consumers are unaffected:
 *  - default        : resource logs hours (click -> DayLogModal via onSelect)
 *  - selectable     : quick-log selection (click -> onToggleSelect, NOT onSelect)
 *  - reviewMode     : approver sees ✓/✗ controls on hover
 *
 * The hours background is an inline gradient computed from the day's actual hours
 * (splitMode="proportional"). Pass splitMode="fixed" to restore the old static
 * 50/50 classes.
 */
export function TimesheetDayCell({
  day,
  readonly      = false,
  selected      = false,
  isUnloggedHighlight = false,
  onSelect,
  onQuickLog,
  // hour split
  splitMode  = 'proportional',
  splitAngle = '135deg',
  // quick-log selection
  selectable = false,
  onToggleSelect,
  // approver day review
  reviewMode = false,
  onApproveDay,
  onRejectDay,
}) {
  const st        = numberValue(day?.workingHours?.ST);
  const ot        = numberValue(day?.workingHours?.OT);
  const cl        = numberValue(day?.leave?.CL);
  const sl        = numberValue(day?.leave?.SL);
  const totalLeave = cl + sl;
  const status     = day?.status;
  const isApproved = status === 'approved';
  const isRejected = status === 'rejected';

  // An approved day is locked for the resource (the server enforces this too), but the
  // approver must still be able to interact with it.
  const locked      = isApproved && !reviewMode;
  // Before the assignment started or after it ended — not a day off, the
  // resource simply wasn't engaged on this project yet/anymore.
  const outOfRange  = Boolean(day?.isOutOfAssignmentRange);
  const canQuickLog = !readonly && !selectable && !outOfRange && !day?.isFuture && !day?.isHoliday && !day?.isWeekOff && !day?.isLogged;

  const gradient = splitMode === 'proportional'
    ? buildDayGradient(day, { angle: splitAngle })
    : null;

  const handleQuickLog = (e) => {
    e.stopPropagation();
    onQuickLog?.(day?.date);
  };

  const handleClick = () => {
    // While selecting for quick log, a click toggles the day instead of opening the
    // log modal.
    if (selectable) {
      onToggleSelect?.(day?.date);
      return;
    }
    onSelect?.(day);
  };

  // In review mode a logged day is clickable (opens the day's review panel) even
  // though the cell is readonly for editing — the approver still needs to select it.
  const reviewClickable = reviewMode && day?.isLogged;

  const cell = (
    <button
      type="button"
      className={getDayClass(day, { isUnloggedHighlight, selected, selectable, reviewMode })}
      style={gradient ? { background: gradient } : undefined}
      disabled={(readonly && !reviewClickable) || day?.isFuture || outOfRange || (locked && !reviewMode)}
      onClick={handleClick}
    >
      {/* top row: date number + day abbr + quick-log clock */}
      <span className="timesheet-day-cell__top">
        <span className="timesheet-day-cell__date">
          {day?.date ? dayjs(day.date).date() : '-'}
          {isRejected && <span className="timesheet-day-cell__reject-dot" />}
        </span>
        <span className="timesheet-day-cell__top-right">
          {canQuickLog && (
            <Tooltip title="Quick log 8h" placement="top">
              <ClockCircleOutlined
                className="timesheet-day-cell__quick-log-icon"
                onClick={handleQuickLog}
              />
            </Tooltip>
          )}
          {!canQuickLog && !outOfRange && !day?.isHoliday && !day?.isWeekOff && !day?.isFuture && (
            <ClockCircleOutlined className="timesheet-day-cell__quick-log-icon timesheet-day-cell__quick-log-icon--logged" />
          )}
        </span>
      </span>

      {/* body: hour labels */}
      <span className="timesheet-day-cell__body">
        {/* An out-of-range day gets no label at all — it isn't a holiday, isn't
            a day off, and "no log" would wrongly imply the resource should
            have logged something. Just a dimmed, unlabeled square. */}
        {!outOfRange && day?.isHoliday && <Tag color="purple" className="timesheet-type-tag">H</Tag>}
        {!outOfRange && day?.isWeekOff && !day?.isLogged && <Text type="secondary" className="timesheet-no-log">Off</Text>}

        {/* Standard */}
        {st > 0 && (
          <span className="timesheet-hour-label">
            <span className="timesheet-hour-value">{formatHoursShort(st)}</span>
            <span className="timesheet-hour-suffix timesheet-hour-st"> s</span>
          </span>
        )}
        {/* Overtime */}
        {ot > 0 && (
          <span className="timesheet-hour-label timesheet-hour-label--ot">
            <span className="timesheet-hour-value">{formatHoursShort(ot)}</span>
            <span className="timesheet-hour-suffix timesheet-hour-ot"> o</span>
          </span>
        )}
        {/* Leave */}
        {totalLeave > 0 && (
          <span
            className={`timesheet-hour-label timesheet-hour-label--leave${gradient ? ' timesheet-hour-label--split-end' : ''}`}
          >
            <span className="timesheet-hour-value">{formatHoursShort(totalLeave)}</span>
            <span className="timesheet-hour-suffix timesheet-hour-leave"> L</span>
          </span>
        )}

        {!outOfRange && !day?.isLogged && !day?.isHoliday && !day?.isWeekOff && !day?.isFuture && (
          <Text type="secondary" className="timesheet-no-log">No log</Text>
        )}
      </span>

      {/* Review verdict badge */}
      {isApproved && <CheckOutlined className="timesheet-day-cell__verdict timesheet-day-cell__verdict--approved" />}
      {isRejected && <CloseCircleOutlined className="timesheet-day-cell__verdict timesheet-day-cell__verdict--rejected" />}

      {/* Approver ✓/✗, revealed on hover. Rendered as spans (not <button>) because
          this cell is itself a <button> and nesting buttons is invalid HTML. */}
      {reviewMode && day?.isLogged && (onApproveDay || onRejectDay) && (
        <span className="timesheet-day-review-actions">
          {onApproveDay && (
            <span
              role="button"
              tabIndex={-1}
              className="ts-approve"
              title="Approve this day"
              onClick={(e) => { e.stopPropagation(); onApproveDay(day?.date); }}
            >
              <CheckOutlined />
            </span>
          )}
          {onRejectDay && (
            <span
              role="button"
              tabIndex={-1}
              className="ts-reject"
              title="Reject this day"
              onClick={(e) => { e.stopPropagation(); onRejectDay(day?.date); }}
            >
              <CloseCircleOutlined />
            </span>
          )}
        </span>
      )}
    </button>
  );

  // D3: hovering a rejected day surfaces WHY it was rejected. The tooltip wraps the
  // whole cell (it used to hang off a 6px dot, which was almost impossible to hit).
  if (isRejected && day?.rejectionReason) {
    return (
      <Tooltip title={day.rejectionReason} placement="top">
        {/* span wrapper: Tooltip needs a hoverable node even when the button is disabled */}
        <span style={{ display: 'block', width: '100%' }}>{cell}</span>
      </Tooltip>
    );
  }
  return cell;
}


// antd's InputNumber infers a display precision from `step` when none is
// given — with step={0.5} that's 1 decimal place ALWAYS, so a whole number
// like 8 shows as "8.0" and an empty/zero field shows "0.0". These just
// format/parse the value as plain JS Number->string, so whole numbers show
// with no decimal at all and a value like 4.5 still shows exactly "4.5".
const formatHoursInput = (value) => (value === undefined || value === null ? '' : `${value}`);
const parseHoursInput = (value) => (value === '' || value === undefined ? undefined : Number(value));

// ── DayLogModal ────────────────────────────────────────────────────────────
// All hour rules (min/max, disabled states, cross-field limits, messages)
// live in timesheetHoursPolicy.js — this component is a thin renderer over
// one computeHoursPolicy(...) snapshot per render. `hoursStep` is a
// back-compat shim for the entry precision; `policy` is the full override
// (merged over DEFAULT_HOURS_POLICY) once company-level config exists.
export function DayLogModal({
  open, day, saving = false, onCancel, onSubmit,
  hoursStep, policy: policyOverride,
}) {
  const [form] = Form.useForm();
  const watchedST = Form.useWatch('ST', form);
  const watchedOT = Form.useWatch('OT', form);
  const watchedLeave = Form.useWatch('Leave', form);

  const policy = useMemo(
    () => ({ ...DEFAULT_HOURS_POLICY, ...(hoursStep ? { step: hoursStep } : null), ...policyOverride }),
    [hoursStep, policyOverride],
  );

  useEffect(() => {
    if (!open) return;
    const dayKind = resolveDayKind(day);
    // No pre-filled Standard default on a fresh day. A partial day (e.g. 2h
    // leave) is valid now, so silently defaulting Standard to 8 would let a
    // resource Save a full day they never actually worked without touching
    // anything — the resource must enter every value themselves. Existing
    // logs still load their saved values.
    const st = numberValue(day?.workingHours?.ST);
    const ot  = numberValue(day?.workingHours?.OT);
    const cl  = numberValue(day?.leave?.CL);
    const sl  = numberValue(day?.leave?.SL);
    const loaded = { ST: st, OT: ot, Leave: cl + sl };
    // A week off's Standard/Leave inputs are disabled, so a legacy value
    // saved before the day was reclassified would otherwise sit there as an
    // unfixable error. Reconcile once on open instead of surfacing it. Pass
    // no changed keys — a genuinely saved partial day (e.g. Standard 6 /
    // Leave 1) must load exactly as saved, not get force-filled to Leave 2
    // as if the user had just edited Standard.
    const patch = reconcileHours(dayKind, loaded, [], policy);
    // No `date` field: the modal title already states the date, so a second
    // read-only copy of it inside the form was pure duplication.
    form.setFieldsValue({ ...loaded, ...patch });
  }, [day, form, open, policy]);

  // One rule per field, all backed by the same policy engine — it is the
  // sole arbiter of what's valid, so live validation and Save-time
  // validation can never disagree.
  const hoursRule = (field) => ({ getFieldValue }) => ({
    validator() {
      const { errorsByField } = computeHoursPolicy(day, {
        ST: getFieldValue('ST'), OT: getFieldValue('OT'), Leave: getFieldValue('Leave'),
      }, policy);
      const message = errorsByField[field];
      return message ? Promise.reject(new Error(message)) : Promise.resolve();
    },
  });

  const revalidateAll = () => {
    form.validateFields(['ST', 'OT', 'Leave']).catch(() => {});
  };

  // UX: Standard and Leave auto-fill each other to share the 8h regular
  // allocation, symmetrically — editing either one fills the other with
  // whatever's left (8 - the field just edited), in both directions. An
  // out-of-range edit (e.g. typing 8888 into Leave) never drives the other
  // field — see reconcileHours for why. Overtime is auto-zeroed only when
  // Standard ends up somewhere other than exactly 8, since Overtime requires
  // a full Standard day (see computeHoursPolicy).
  const handleValuesChange = (_changed, allValues) => {
    const patch = reconcileHours(resolveDayKind(day), allValues, Object.keys(_changed), policy);
    if (patch) form.setFieldsValue(patch);
    revalidateAll();
  };

  const submit = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch (errorInfo) {
      // Scoped to this modal's own portal root — a plain document-wide query
      // could otherwise focus an unrelated errored field elsewhere on the
      // page (e.g. NotesPanel) instead of this form's.
      const root = document.querySelector('.timesheet-log-modal') ?? document;
      scrollToFirstFormError(errorInfo, form, { root });
      return;
    }
    const stHours = numberValue(values.ST);
    const otHours = numberValue(values.OT);
    const leaveHours = numberValue(values.Leave);
    const typesLogged = [stHours, otHours, leaveHours].filter((h) => h > 0).length;
    onSubmit?.({
      // Straight off the day being edited — the date was never user-editable
      // (the old form field was permanently disabled), so there is no form
      // value to read it back from.
      date:         day?.date,
      // More than one type present -> 'mixed' (the calendar cell renders the
      // actual gradient split off the raw hour numbers regardless of this
      // string). Exactly one type present -> name THAT type, not just
      // 'working' whenever Leave happens to be 0 — that used to be the
      // fallback for a week-off day logged purely as Overtime, so it was
      // saved (and rendered) as if it were an ordinary Standard day, blue
      // instead of Overtime's green.
      logType:      typesLogged > 1 ? 'mixed' : otHours > 0 ? 'overtime' : leaveHours > 0 ? 'leave' : 'working',
      workingHours: { ST: stHours, OT: otHours },
      leave:        { CL: leaveHours, SL: 0 },
      isHoliday:    Boolean(day?.isHoliday),
      isWeekOff:    Boolean(day?.isWeekOff),
      notes:        '',
    });
  };

  // A holiday needs no log at all. A week off is different: the day is still
  // loggable, just entirely as Overtime — Standard/Leave don't apply, so
  // only a holiday hides the form outright.
  const isLocked = day?.isHoliday;
  const isWeekOff = Boolean(day?.isWeekOff) && !isLocked;
  const dateLabel = day?.date ? dayjs(day.date).format('MMM DD, YYYY') : 'Log time';
  const snapshot = useMemo(
    () => computeHoursPolicy(day, { ST: watchedST, OT: watchedOT, Leave: watchedLeave }, policy),
    [day, watchedST, watchedOT, watchedLeave, policy],
  );

  return (
    <Modal
      rootClassName="timesheet-log-modal"
      title={
        <Space>
          <Text strong>{dateLabel}</Text>
          {day?.isHoliday && <Tag color="purple">Holiday</Tag>}
          {day?.isWeekOff  && <Tag>Week Off</Tag>}
        </Space>
      }
      open={open}
      onCancel={onCancel}
      onOk={submit}
      confirmLoading={saving}
      okText="Save"
      cancelText="Cancel"
      destroyOnHidden
      width={340}
      footer={(_, { OkBtn, CancelBtn }) => (
        <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
          <CancelBtn />
          {!isLocked && <OkBtn />}
        </Space>
      )}
    >
      {/* Correcting a rejected day. This modal is used rarely (once a month) by
          non-technical resources, so the block reads as a plain problem → steps
          pair: WHY it came back, then exactly WHAT to do. Kept to two compact
          strips so the form below stays in view. */}
      {day?.rejectionReason && (
        <div className="timesheet-log-reject">
          <div className="timesheet-log-reject__note">
            <CloseCircleOutlined className="timesheet-log-reject__icon" />
            <div className="timesheet-log-reject__body">
              <span className="timesheet-log-reject__title">Sent back by your reviewer</span>
              <span className="timesheet-log-reject__reason">{day.rejectionReason}</span>
            </div>
          </div>
          <div className="timesheet-log-reject__proof">
            <UploadOutlined className="timesheet-log-reject__proof-icon" />
            <div className="timesheet-log-reject__body">
              <span className="timesheet-log-reject__title timesheet-log-reject__title--proof">
                To fix it
              </span>
              <ol className="timesheet-log-reject__steps">
                <li>Correct the hours below.</li>
                <li>Replace the proof / screenshot so it matches.</li>
                <li>Save, then re-submit your timesheet.</li>
              </ol>
            </div>
          </div>
        </div>
      )}
      {isLocked ? (
        <Alert
          type="info"
          showIcon
          message="Holiday — no manual log required."
        />
      ) : (
        <>
          {isWeekOff && (
            <Alert
              type="info"
              showIcon
              message="Week off — overtime only."
              style={{ marginBottom: 12 }}
            />
          )}
          <Form
            form={form}
            layout="vertical"
            className="timesheet-log-form"
            validateTrigger={['onChange', 'onBlur']}
            onValuesChange={handleValuesChange}
          >
            {/* Standard and Leave don't apply on a week off at all — only Overtime is
                loggable, so both are shown disabled (greyed, per the reference
                design) rather than hidden: the resource can still see them sitting
                at 0 instead of the row silently disappearing. `disabled` comes
                straight off the policy snapshot and still hard-blocks typing for a
                week-off/holiday/maxed-out field. Deliberately NOT passing min/max to
                the InputNumber itself, though: antd's InputNumber gates its own
                onChange on the typed value being in-range, so a fully out-of-range
                keystroke (e.g. typing "287") never reaches the Form at all — the
                input LOOKS like it updated but the real field value silently stays
                at whatever was last valid, and that stale value is what Save would
                actually submit. With no min/max configured, every keystroke reaches
                the Form and re-runs the hoursRule validator below, so the error
                message appears immediately and Save always sees exactly what's on
                screen — never a silently-diverged value.

                Each field also gets a short `extra` note, but ONLY while it's
                actually disabled — never as a constant caption while the field is
                usable, and never alongside an active error (the error already
                explains itself). */}
            <Form.Item
              name="ST"
              label={<span style={{ color: '#1d4ed8', fontWeight: 600 }}>Standard <span style={{ fontWeight: 400, color: '#6b7280' }}>hrs</span></span>}
              dependencies={['Leave']}
              rules={[hoursRule('ST')]}
              extra={
                !snapshot.errorsByField.ST && snapshot.fields.ST.disabled
                  ? 'Disabled on a week off.'
                  : undefined
              }
            >
              <InputNumber step={snapshot.fields.ST.step} disabled={snapshot.fields.ST.disabled} style={{ width: '100%' }}
                formatter={formatHoursInput}
                parser={parseHoursInput}
                addonAfter={<span style={{ color: '#1d4ed8', fontWeight: 700 }}>s</span>}
              />
            </Form.Item>
            <Form.Item
              name="OT"
              label={<span style={{ color: '#15803d', fontWeight: 600 }}>Overtime <span style={{ fontWeight: 400, color: '#6b7280' }}>hrs</span></span>}
              dependencies={['ST']}
              rules={[hoursRule('OT')]}
              extra={
                !snapshot.errorsByField.OT && snapshot.fields.OT.disabled
                  ? 'Complete 8 standard hours to log overtime.'
                  : undefined
              }
            >
              <InputNumber step={snapshot.fields.OT.step} disabled={snapshot.fields.OT.disabled} style={{ width: '100%' }}
                formatter={formatHoursInput}
                parser={parseHoursInput}
                addonAfter={<span style={{ color: '#15803d', fontWeight: 700 }}>o</span>}
              />
            </Form.Item>
            <Form.Item
              name="Leave"
              label={<span style={{ color: '#b45309', fontWeight: 600 }}>Leave <span style={{ fontWeight: 400, color: '#6b7280' }}>hrs</span></span>}
              dependencies={['ST']}
              rules={[hoursRule('Leave')]}
              extra={
                !snapshot.errorsByField.Leave && snapshot.fields.Leave.disabled
                  ? (isWeekOff ? 'Disabled on a week off.' : 'Maximum reached — 8 regular hours allocated.')
                  : undefined
              }
            >
              <InputNumber step={snapshot.fields.Leave.step} disabled={snapshot.fields.Leave.disabled} style={{ width: '100%' }}
                formatter={formatHoursInput}
                parser={parseHoursInput}
                addonAfter={<span style={{ color: '#b45309', fontWeight: 700 }}>L</span>}
              />
            </Form.Item>
          </Form>
          {/* No summary block and no per-field descriptive hints (e.g. "Overtime
              up to 16 hrs.", "Maximum available leave: 2 hrs.") — those read as
              confusing noise next to the fields. Each Form.Item's `rules`
              validator is the only messaging: silent while valid, an explicit
              error the moment a value is actually invalid. */}
        </>
      )}
    </Modal>
  );
}

// ── QuickLogSelectionBar ───────────────────────────────────────────────────
/**
 * Footer bar for quick-log selection.
 *
 * Quick Log does not submit blind and does not open a modal: it strokes every eligible
 * day in the calendar, the user clicks cells to deselect any they don't want, and this
 * bar confirms only what is still selected.
 */
export function QuickLogSelectionBar({
  count = 0,
  total = 0,
  hours = 8,
  loading = false,
  onConfirm,
  onCancel,
  onSelectAll,
  onSelectNone,
}) {
  return (
    <div className="timesheet-quicklog-bar">
      <Space size={8} wrap>
        <Text strong>
          {count === 0
            ? 'No days selected'
            : `Log ${hours}h standard on ${count} day${count === 1 ? '' : 's'}`}
        </Text>
        {total > 0 && (
          <Text type="secondary" style={{ fontSize: 12 }}>
            {count} of {total} eligible · click a day to deselect it
          </Text>
        )}
      </Space>
      <Space size={6}>
        <Button size="small" onClick={onSelectAll} disabled={count === total}>Select all</Button>
        <Button size="small" onClick={onSelectNone} disabled={count === 0}>Clear</Button>
        <Button size="small" onClick={onCancel}>Cancel</Button>
        <Button
          size="small"
          type="primary"
          icon={<ClockCircleOutlined />}
          loading={loading}
          disabled={count === 0}
          onClick={onConfirm}
        >
          Confirm
        </Button>
      </Space>
    </div>
  );
}

// ── RejectionReasonModal ───────────────────────────────────────────────────
export function RejectionReasonModal({ open = false, loading = false, onSave, onCancel }) {
  const [reason, setReason] = useState('');

  useEffect(() => { if (open) setReason(''); }, [open]);

  return (
    <Modal
      title="Rejection Reason"
      open={open}
      onCancel={onCancel}
      footer={
        <Space>
          <Button onClick={onCancel}>Cancel</Button>
          <Button type="primary" danger disabled={!reason.trim()} loading={loading} onClick={() => onSave?.(reason.trim())}>
            Reject
          </Button>
        </Space>
      }
      destroyOnHidden
    >
      <Form layout="vertical">
        <Form.Item
          label={<span><span style={{ color: '#ff4d4f' }}>*</span> Comments</span>}
          help={!reason.trim() ? 'Rejection reason is required' : ''}
          validateStatus={!reason.trim() ? 'error' : ''}
        >
          <Input.TextArea
            rows={4} maxLength={1000} showCount
            placeholder="Enter reason for rejection..."
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
}

// ── QuickLogBar (kept for backward compat) ─────────────────────────────────
export function QuickLogBar({ loading = false, disabled = false, onQuickLog }) {
  return (
    <Card size="small" className="timesheet-quick-log">
      <Space wrap>
        <AppButton
          variant="primary"
          icon={<ClockCircleOutlined />}
          loading={loading}
          disabled={disabled}
          onClick={() => onQuickLog?.('businessDays')}
        >
          Quick Log (8h s)
        </AppButton>
      </Space>
    </Card>
  );
}

// Extension → icon + colour tone for the attached-proof row. Deliberately a
// local map rather than a shared export: AddFormV1 has its own copy bound to
// its own v1-* class names, and the two rows are look-alikes, not one
// component — that one is coupled to the dynamic-form field system. The
// extension parsing is shared (getFileExtension), the JSX is not.
const PROOF_ICON_BY_EXT = {
  '.pdf':  [FilePdfOutlined,   'pdf'],
  '.doc':  [FileWordOutlined,  'word'],
  '.docx': [FileWordOutlined,  'word'],
  '.xls':  [FileExcelOutlined, 'excel'],
  '.xlsx': [FileExcelOutlined, 'excel'],
  '.csv':  [FileExcelOutlined, 'excel'],
  '.png':  [FileImageOutlined, 'image'],
  '.jpg':  [FileImageOutlined, 'image'],
  '.jpeg': [FileImageOutlined, 'image'],
  '.gif':  [FileImageOutlined, 'image'],
  '.webp': [FileImageOutlined, 'image'],
};

function ProofTypeIcon({ fileName }) {
  const [Icon, tone] = PROOF_ICON_BY_EXT[getFileExtension(fileName)] ?? [FileOutlined, 'default'];
  return <Icon className={`timesheet-proof__type-icon timesheet-proof__type-icon--${tone}`} />;
}

// ── ProofUploadPreview ─────────────────────────────────────────────────────
// This card is the resource's UPLOAD CONTROL, not an evidence viewer — the
// manager's evidence view is the review page. So an attached proof reads as a
// compact one-line receipt (type icon, name, size/status, View) rather than a
// full-bleed screenshot: the old inline thumbnail cost ~260px of the column and
// duplicated the caller's own preview. The full render belongs in whatever
// viewer the caller opens from `onPreview` — on the timesheet pages that is a
// right-side drawer, so the calendar stays visible beside the proof.
//
// @param {object}   [proof]      { fileName, size?, pending? } — only a freshly
//   picked File carries `size`/`pending`; a saved proof payload has neither
//   (no size, no uploaded-at), hence the degraded meta line.
// @param {function} [onPreview]  called with `proof`; omit to hide View.
// @param {function} [onRemove]   called with `proof`; omit to hide the delete
//   button. Intended for a not-yet-uploaded pick — clearing a saved proof would
//   need a backend it does not have.
// @param {boolean}  [required]   marks the card title with the standard red
//   asterisk. Proof is mandatory before a timesheet can be submitted, and an
//   asterisk states that up front the way every other required field does —
//   quieter than a standing warning banner under the card.
// @param {string}   [previewUrl] @deprecated — drove the removed inline
//   thumbnail. Still accepted so existing callers keep working; ignored.
export function ProofUploadPreview({
  proof, uploading = false, disabled = false, required = false,
  // eslint-disable-next-line no-unused-vars -- previewUrl kept for old callers
  onUpload, onPreview, onRemove, previewUrl,
}) {
  const hasFile = Boolean(proof?.fileName);
  const sizeText = proof?.size ? formatFileSize(proof.size) : '';
  const extText = getFileExtension(proof?.fileName ?? '').replace('.', '').toUpperCase();
  const statusText = proof?.pending ? 'Selected — uploads on submit' : 'Previously uploaded';
  // Size wins over the extension label whenever it is known, so this upgrades
  // by itself if the backend ever starts returning a size for saved proofs.
  const metaText = [sizeText || extText, statusText].filter(Boolean).join(' • ');
  // Read-only hosts never get an upload control; nor does an attached file that
  // the row already lets you delete (see the Upload block below).
  const showUpload = !disabled && !(hasFile && onRemove);

  return (
    <Card
      className={`timesheet-proof${hasFile ? ' timesheet-proof--filled' : ''}`}
      size="small"
      title={<>Proof / screenshot{required && <span className="timesheet-proof__required"> *</span>}</>}
    >
      {hasFile ? (
        <div className="timesheet-proof__file">
          <span className="timesheet-proof__file-icon"><ProofTypeIcon fileName={proof.fileName} /></span>
          <span className="timesheet-proof__file-info">
            <span className="timesheet-proof__file-name" title={proof.fileName}>{proof.fileName}</span>
            <span className="timesheet-proof__file-meta">{metaText}</span>
          </span>
          <Space size={6} className="timesheet-proof__file-actions">
            {onPreview && (
              <Button
                size="small"
                icon={<EyeOutlined />}
                className="timesheet-proof__view-btn"
                onClick={() => onPreview(proof)}
              >
                View
              </Button>
            )}
            {onRemove && (
              <Tooltip title="Remove">
                <Button
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  aria-label={`Remove ${proof.fileName}`}
                  className="timesheet-proof__delete-btn"
                  onClick={() => onRemove(proof)}
                />
              </Tooltip>
            )}
          </Space>
        </div>
      ) : (
        <Text type="secondary" style={{ fontSize: 12 }}>Upload Proof</Text>
      )}
      {/* Replace is dropped once the row carries a delete button: remove then
          upload is the same two clicks, so a third control for it is noise.
          It stays for a file with no delete — a SAVED proof, which has no
          delete API — because Replace is then the only way to change it, and a
          rejected month explicitly asks the resource to do exactly that. */}
      {showUpload && (
        <Upload
          beforeUpload={(file) => { onUpload?.(file); return false; }}
          maxCount={1}
          showUploadList={false}
          disabled={disabled}
          accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
        >
          <Button icon={<UploadOutlined />} loading={uploading} block size="small" style={{ marginTop: 6 }}>
            {hasFile ? 'Replace proof' : 'Upload proof'}
          </Button>
        </Upload>
      )}
      {/* Only tells the user anything before a file exists — hidden by CSS once
          the row is showing what was actually attached. */}
      <Text type="secondary" className="timesheet-proof__hint" style={{ fontSize: 11 }}>
        Supports: .pdf, .doc, .docx, .png, .jpg — max 10 MB
      </Text>
    </Card>
  );
}

// ── NotesPanel ─────────────────────────────────────────────────────────────
export function NotesPanel({ value, disabled = false, onChange, label = 'Notes / Comments', maxLength = 1000 }) {
  const count = (value ?? '').length;
  return (
    <Card className="timesheet-notes" size="small" title={label}>
      <div className="timesheet-notes__field">
        <Input.TextArea
          className="timesheet-notes__textarea"
          rows={5}
          maxLength={maxLength}
          value={value}
          disabled={disabled}
          onChange={(e) => onChange?.(e.target.value)}
          placeholder="Add notes or comments..."
        />
      </div>
      {/* Static count row (replaces antd showCount, which overlaps the card edge) */}
      <div className="timesheet-notes__count">{count} / {maxLength}</div>
    </Card>
  );
}

// ── ApprovalActionBar ──────────────────────────────────────────────────────
// rejectionMode:
//   'modal'  (default) — Reject opens RejectionReasonModal; the resource-facing
//            Submit/Re-Submit bar (TimesheetCalendarPage) uses this, unchanged.
//   'inline' — manager review mode: renders as a "Review Notes" card with an
//            always-visible mandatory-comment textarea instead of a modal, plus
//            the decided-state "This timesheet is X." + Reopen (TimesheetReviewPage).
export function ApprovalActionBar({
  status,
  loading    = false,
  readonly   = false,
  rejectionReason,
  rejectionMode = 'modal',
  title = 'Review Notes',
  hint = 'Review comment is mandatory while rejecting',
  approveDisabled = false,
  approveDisabledReason,
  rejectDisabled = false,
  rejectDisabledReason,
  onSubmit,
  onApprove,
  onReject,
  onReopen,
}) {
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectLoading, setRejectLoading] = useState(false);
  const [inlineComment, setInlineComment] = useState('');

  const pendingManagerAction = ['submitted', 'resubmitted', 'reopened', 'save_draft'].includes(status);
  const isRejected  = status === 'rejected';
  const isApproved  = status === 'approved';
  const isSubmitted = status === 'submitted' || status === 'resubmitted';
  // Reopened = the approver handed the month back. Like rejected, it is the
  // RESOURCE's turn again, so it must offer an enabled Re-Submit — not the
  // disabled "Submitted" a submitted-like state would produce.
  const isReopened  = status === 'reopened';
  const isDraft     = !status || status === 'draft' || status === 'not_submitted';

  const handleReject = async (reason) => {
    setRejectLoading(true);
    try { await onReject?.(reason); setRejectOpen(false); }
    finally { setRejectLoading(false); }
  };

  // Reopening hands the month back to the resource to correct and re-submit —
  // it does NOT return it to "submitted", which is what this used to claim.
  // It is also refused outright once the timesheet's finance record has been
  // invoiced or paid, so the copy stops short of promising it will work.
  const confirmReopen = () => Modal.confirm({
    title: 'Reopen timesheet?',
    content: 'This returns the month to the resource to correct and re-submit. '
      + 'It cannot be reopened once its invoice or payment has been processed.',
    okText: 'Reopen',
    onOk: onReopen,
  });

  const submitInlineReject = () => {
    const reason = inlineComment.trim();
    if (!reason) { message.error('A review comment is required to reject'); return; }
    onReject?.(reason);
  };

  if (rejectionMode === 'inline') {
    return (
      <Card size="small" className="timesheet-review-notes-card">
        <div className="timesheet-review-notes-card__head">
          <Text strong>{title}</Text>
          <Text type="secondary" className="timesheet-review-notes-card__hint">
            <InfoCircleOutlined /> {hint}
          </Text>
        </div>

        {rejectionReason && isRejected && (
          <Alert type="error" showIcon message="Rejected" description={rejectionReason} className="timesheet-review-notes-card__alert" />
        )}

        {isSubmitted ? (
          <>
            <Input.TextArea
              rows={3}
              maxLength={1000}
              value={inlineComment}
              onChange={(e) => setInlineComment(e.target.value)}
              placeholder="Add your note"
              className="timesheet-review-notes-card__input"
            />
            <div className="timesheet-review-notes-card__actions">
              <Tooltip title={rejectDisabled ? rejectDisabledReason : ''}>
                <Button danger loading={loading} disabled={rejectDisabled} onClick={submitInlineReject}>Reject</Button>
              </Tooltip>
              <Tooltip title={approveDisabled ? approveDisabledReason : ''}>
                <Button type="primary" loading={loading} disabled={approveDisabled} onClick={onApprove}>Approve</Button>
              </Tooltip>
            </div>
          </>
        ) : (
          <div className="timesheet-review-notes-card__decided">
            <Text type="secondary">This timesheet is {status}.</Text>
            {(isApproved || isRejected) && onReopen ? (
              <Button icon={<InfoCircleOutlined />} loading={loading} onClick={confirmReopen}>Reopen</Button>
            ) : null}
          </div>
        )}
      </Card>
    );
  }

  // Submit button label + disabled state.
  //
  // The label follows one rule: has this month been submitted before? If so the
  // action is a RE-submission. "Submit" is therefore only ever shown on a month
  // that has never left the resource's hands (draft / not submitted); every
  // hand-back state — rejected or reopened — reads "Re-Submit".
  let submitLabel = 'Submit';
  let submitDisabled = false;
  if (isSubmitted) { submitLabel = status === 'resubmitted' ? 'Re-Submitted' : 'Submitted'; submitDisabled = true; }
  if (isRejected || isReopened) { submitLabel = 'Re-Submit'; submitDisabled = false; }
  if (isApproved) { submitLabel = 'Approved'; submitDisabled = true; }

  return (
    <>
      {rejectionReason && isRejected && (
        <Alert type="error" showIcon message="Rejected" description={rejectionReason} style={{ marginBottom: 8 }} />
      )}
      <div className="timesheet-action-bar">
        {/* Resource submit / re-submit — always visible, disabled when already submitted */}
        {(isDraft || isRejected || isReopened || isSubmitted || isApproved) && onSubmit && (
          <AppButton
            variant="primary"
            icon={<SendOutlined />}
            loading={loading}
            disabled={submitDisabled}
            onClick={!submitDisabled ? onSubmit : undefined}
            block
          >
            {submitLabel}
          </AppButton>
        )}

        {/* Manager approve */}
        {pendingManagerAction && onApprove && (
          <AppButton variant="primary" icon={<CheckOutlined />} loading={loading} onClick={onApprove}>
            Approve
          </AppButton>
        )}
        {/* Manager reject */}
        {pendingManagerAction && onReject && (
          <Button danger loading={loading} onClick={() => setRejectOpen(true)}>Reject</Button>
        )}
        {/* Reopen */}
        {(isApproved || isRejected) && onReopen && (
          <Button icon={<InfoCircleOutlined />} loading={loading} onClick={confirmReopen}>Reopen</Button>
        )}
      </div>

      <RejectionReasonModal
        open={rejectOpen}
        loading={rejectLoading}
        onSave={handleReject}
        onCancel={() => setRejectOpen(false)}
      />
    </>
  );
}
